import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Plus,
  Trash2,
  Mail,
  Send,
  Pencil,
  ChevronDown,
  Lock,
  LogOut,
  ExternalLink,
  Check,
  CheckCircle2,
  Loader2,
  Upload,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PageHeader, SectionTitle } from './PageHeader';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { Dialog, DialogContent } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
} from './ui/alert-dialog';
import { useAuth } from '@/contexts/AuthContext';
import { sortClientTeamsByName } from '@/utils/sortClientTeamsByName';
import {
  type ClientTeamSearchRow,
  type TeamMemberRole,
  archiveTeamBrand,
  createTeamBrand,
  getCurrentUser,
  getTeamBrandsSearch,
  getTeamMembersSearch,
  getTeamsSearch,
  inviteTeamMember,
  removeTeamMember,
  selectClientTeam,
  uploadTeamBrandLogo,
  updateTeam,
  updateTeamBrand,
  updateTeamMember,
} from '@/api/beatlyApi';
import {
  TeamBrandSettingsSection,
  type TeamBrandSettingsMember,
  type TeamBrandSettingsSetMembers,
} from '@/components/TeamBrandSettingsSection';
import { getTokenFromStorage, updateAuthInStorage } from '@/utils/auth';
import { showError } from '@/utils/alert';
import { BEATLY_PRIVACY_POLICY_URL, BEATLY_TERMS_OF_SERVICE_URL } from '@/config/legal';
import { mapBrandSettingsFromApi } from '@/utils/teamMemberBrandSettings';
import {
  buildTeamRoleOptions,
  canManageReviewerAssignments,
  type TeamRoleOption,
} from '@/utils/teamRoleOptions';

interface Brand {
  id: string; // brand_id
  name: string;
  logo: string;
  url?: string;
  description?: string;
  hasLogo?: boolean;
}

type ExternalBrand = {
  name: string;
  domain: string;
  logo?: string;
};

function normalizeExternalUrl(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';

  // Fix common malformed prefixes like `https//` or `http//`.
  const fixed = trimmed.replace(/^https?:\/(?!\/)/i, (m) => `${m}/`);
  const withProtocol = /^https?:\/\//i.test(fixed) ? fixed : `https://${fixed.replace(/^\/+/, '')}`;
  return withProtocol;
}

interface Member extends TeamBrandSettingsMember {
  role: TeamMemberRole;
  dateInvited?: string;
  isCurrentUser?: boolean;
  avatar?: string | null;
  isCurationReviewer: boolean;
  isDraftReviewer: boolean;
}

/* ─── Role Dropdown ─── */

function isTeamMemberRole(value: string): value is TeamMemberRole {
  return value === 'ADMIN' || value === 'MARKETING_MANAGER' || value === 'EXTERNAL_PARTNER';
}

/**
 * Team role picker. Compact variant aligns with the members table and portals
 * via `position: fixed`. Field variant matches modal inputs, shows per-role
 * descriptions during invite, and anchors absolutely under the trigger so a
 * parent Dialog transform cannot misplace the menu.
 */
const RoleDropdown = ({
  value,
  onChange,
  disabled,
  options,
  placeholder,
  variant = 'compact',
  showDescriptions = false,
  ariaLabel,
}: {
  value: TeamMemberRole | '';
  onChange: (role: TeamMemberRole) => void;
  disabled?: boolean;
  options: TeamRoleOption[];
  placeholder?: string;
  variant?: 'compact' | 'field';
  showDescriptions?: boolean;
  ariaLabel?: string;
}) => {
  const [open, setOpen] = useState(false);
  const selectedLabel = value
    ? (options.find((r) => r.value === value)?.label ?? value)
    : (placeholder ?? '');
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null);
  const isFieldVariant = variant === 'field';

  // Field menus skip the compact full-screen overlay; close on outside mousedown instead.
  useEffect(() => {
    if (!open || !isFieldVariant) return;

    function onPointerDown(event: MouseEvent) {
      const root = rootRef.current;
      if (root && event.target instanceof Node && !root.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open, isFieldVariant]);

  useLayoutEffect(() => {
    if (!open || isFieldVariant) {
      setMenuStyle(null);
      return;
    }

    function recompute() {
      const trigger = triggerRef.current;
      const menu = menuRef.current;
      if (!trigger || !menu) return;

      const rect = trigger.getBoundingClientRect();

      const contentWidth = Math.ceil(menu.scrollWidth || menu.offsetWidth || 0);
      const desiredWidth = Math.min(220, Math.max(rect.width, Math.max(140, contentWidth)));
      const menuHeight = menu.offsetHeight || 140;
      const margin = 8;

      let left = rect.right - desiredWidth;
      left = Math.max(margin, Math.min(left, window.innerWidth - desiredWidth - margin));

      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < menuHeight + margin;

      const top = openUp
        ? Math.max(margin, rect.top - menuHeight - margin)
        : Math.min(window.innerHeight - menuHeight - margin, rect.bottom + margin);

      setMenuStyle({
        position: 'fixed',
        top,
        left,
        width: desiredWidth,
        maxHeight: Math.max(120, window.innerHeight - margin * 2),
        overflowY: 'auto',
        zIndex: 50,
      });
    }

    recompute();
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, true);
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, true);
    };
  }, [open, isFieldVariant]);

  const triggerClassName = isFieldVariant
    ? `flex items-center justify-between w-full h-12 rounded-xl border border-gray-200 bg-white px-4 text-base md:text-sm transition-all ${
        value ? 'text-[#101828]' : 'text-[#667085]'
      } ${
        disabled
          ? 'opacity-60 cursor-not-allowed'
          : 'hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#be43ff]/30 focus:border-[#be43ff]'
      }`
    : `flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-sm text-[#344054] transition-all ${
        disabled
          ? 'opacity-60 cursor-not-allowed'
          : 'hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#be43ff]/30 focus:border-[#be43ff]'
      }`;

  const menuClassName =
    'bg-white rounded-xl border border-gray-100 shadow-lg shadow-black/5 overflow-hidden py-1';

  const roleOptions = options.map((role) => {
    const isSelected = role.value === value;
    return (
      <button
        key={role.value}
        type="button"
        role="option"
        aria-selected={isSelected}
        onClick={() => {
          onChange(role.value);
          setOpen(false);
        }}
        className={`w-full text-left px-3 hover:bg-gray-50 transition-colors ${
          showDescriptions ? 'py-2.5' : 'py-1.5'
        } text-sm ${
          isSelected ? 'text-[#be43ff] bg-[#be43ff]/5' : 'text-[#344054]'
        }`}
      >
        {showDescriptions && role.description ? (
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="font-medium text-[#101828]">{role.label}</div>
              <div className="text-xs text-[#667085] mt-0.5 leading-snug">{role.description}</div>
            </div>
            {isSelected && <Check className="w-3.5 h-3.5 shrink-0 mt-0.5" />}
          </div>
        ) : (
          <>
            {role.label}
            {isSelected && <Check className="inline w-3 h-3 ml-2" />}
          </>
        )}
      </button>
    );
  });

  return (
    <div ref={rootRef} className={isFieldVariant ? 'relative w-full' : 'relative'}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        className={triggerClassName}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className="truncate text-left">{selectedLabel}</span>
        <ChevronDown
          className={`text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''} ${
            isFieldVariant ? 'w-4 h-4' : 'w-3.5 h-3.5'
          }`}
        />
      </button>
      {open && isFieldVariant && (
        <div
          ref={menuRef}
          role="listbox"
          className={`${menuClassName} absolute left-0 right-0 top-full z-50 mt-1`}
        >
          {roleOptions}
        </div>
      )}
      {open && !isFieldVariant && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            ref={menuRef}
            role="listbox"
            style={menuStyle ?? { position: 'fixed', top: 0, left: 0, zIndex: 50 }}
            className={menuClassName}
          >
            {roleOptions}
          </div>
        </>
      )}
    </div>
  );
};

/* ─── Member Avatar with Upload/Edit/Delete ─── */

const initialsColors = [
  { bg: '#EDE9FE', text: '#7C3AED' },
  { bg: '#DBEAFE', text: '#2563EB' },
  { bg: '#D1FAE5', text: '#059669' },
  { bg: '#FEF3C7', text: '#D97706' },
  { bg: '#FCE7F3', text: '#DB2777' },
  { bg: '#E0E7FF', text: '#4F46E5' },
];

function getInitials(name: string) {
  const safe = (name ?? '').trim();
  if (!safe) return '?';
  return safe
    .split(/\s+/)
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function getInitialsColor(name: string) {
  const safe = (name ?? '').trim();
  const code = safe ? safe.toLowerCase().charCodeAt(0) : 0;
  const idx = Number.isFinite(code) ? code % initialsColors.length : 0;
  return initialsColors[idx] ?? initialsColors[0];
}

const MemberAvatar = ({
  member,
}: {
  member: Member;
}) => {
  const displayName = (member.name ?? '').trim() || (member.email ?? '').trim() || 'User';
  const initials = getInitials(displayName);
  const color = getInitialsColor(displayName);

  return (
    <div className="w-9 h-9 rounded-xl shrink-0 overflow-hidden">
      {member.avatar ? (
        <img src={member.avatar} alt={displayName} className="w-full h-full object-cover" />
      ) : (
        <div
          className="w-full h-full flex items-center justify-center select-none"
          style={{ backgroundColor: color.bg, color: color.text, fontSize: 12, fontWeight: 600, letterSpacing: '0.02em' }}
        >
          {initials}
        </div>
      )}
    </div>
  );
};

/* ─── Team Page ─── */

interface TeamPageProps {
  userName?: string;
}

export const TeamPage: React.FC<TeamPageProps> = ({ userName: _userName = 'Noemi Miki' }) => {
  const { t } = useTranslation();
  const teamRoleOptions = useMemo(() => buildTeamRoleOptions(t, false), [t]);
  const inviteTeamRoleOptions = useMemo(() => buildTeamRoleOptions(t, true), [t]);
  const { user, setUser, logout } = useAuth();
  const [teams, setTeams] = useState<ClientTeamSearchRow[]>([]);
  const teamsSortedByName = useMemo(() => sortClientTeamsByName(teams), [teams]);
  const [isTeamPickerOpen, setIsTeamPickerOpen] = useState(false);
  const [isSwitchingTeam, setIsSwitchingTeam] = useState(false);
  const teamPickerTriggerRef = useRef<HTMLButtonElement | null>(null);
  const teamPickerMenuRef = useRef<HTMLDivElement | null>(null);
  const [teamPickerMenuStyle, setTeamPickerMenuStyle] = useState<React.CSSProperties | null>(null);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [currentTeamId, setCurrentTeamId] = useState<string | null>(null);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [isLoadingTeamData, setIsLoadingTeamData] = useState(false);
  const [isLeavingTeam, setIsLeavingTeam] = useState(false);
  const [updatingMemberId, setUpdatingMemberId] = useState<string | null>(null);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);

  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [inviteMemberName, setInviteMemberName] = useState('');
  const [inviteMemberEmail, setInviteMemberEmail] = useState('');
  const [inviteMemberRole, setInviteMemberRole] = useState<TeamMemberRole | ''>('');
  const [isInvitingMember, setIsInvitingMember] = useState(false);
  const [remindingMemberId, setRemindingMemberId] = useState<string | null>(null);

  const [isAddBrandOpen, setIsAddBrandOpen] = useState(false);
  const [brandCreateName, setBrandCreateName] = useState('');
  const [brandSearchTerm, setBrandSearchTerm] = useState('');
  const [showBrandSearchResults, setShowBrandSearchResults] = useState(false);
  const [brandsExternal, setBrandsExternal] = useState<ExternalBrand[]>([]);
  const [isCreatingBrand, setIsCreatingBrand] = useState(false);
  const [isSearchingBrands, setIsSearchingBrands] = useState(false);
  const [deleteBrandId, setDeleteBrandId] = useState<string | null>(null);
  const [isArchivingBrand, setIsArchivingBrand] = useState(false);

  const [isEditTeamNameOpen, setIsEditTeamNameOpen] = useState(false);
  const [editTeamName, setEditTeamName] = useState('');
  const [isUpdatingTeamName, setIsUpdatingTeamName] = useState(false);

  const [isEditBrandOpen, setIsEditBrandOpen] = useState(false);
  const [editingBrandId, setEditingBrandId] = useState<string | null>(null);
  const [editBrandName, setEditBrandName] = useState('');
  const [editBrandDescription, setEditBrandDescription] = useState('');
  const [editBrandUrl, setEditBrandUrl] = useState('');
  const [editBrandSearchTerm, setEditBrandSearchTerm] = useState('');
  const [editBrandShowSearchResults, setEditBrandShowSearchResults] = useState(false);
  const [editBrandExternalResults, setEditBrandExternalResults] = useState<ExternalBrand[]>([]);
  const [isSearchingEditBrandLogos, setIsSearchingEditBrandLogos] = useState(false);
  const [isUpdatingBrand, setIsUpdatingBrand] = useState(false);
  const [editBrandLogoPreview, setEditBrandLogoPreview] = useState<string>('');
  const [editBrandLogoUpdate, setEditBrandLogoUpdate] = useState<
    | { kind: 'none' }
    | { kind: 'brandfetch'; logoUrl: string }
    | { kind: 'file'; file: File }
  >({ kind: 'none' });
  const [isUploadingEditBrandLogo, setIsUploadingEditBrandLogo] = useState(false);
  const editBrandLogoInputRef = useRef<HTMLInputElement | null>(null);

  const setEditBrandLogoPreviewSafely = (next: string) => {
    setEditBrandLogoPreview((prev) => {
      if (prev && prev.startsWith('blob:') && prev !== next) {
        URL.revokeObjectURL(prev);
      }
      return next;
    });
  };

  const currentUserId = typeof user?.user_id === 'string' ? user.user_id : '';
  const currentUserTeamId = typeof user?.team?.team_id === 'string' ? user.team.team_id : null;
  const isAdmin = useMemo(() => String((user?.team as Record<string, unknown> | undefined)?.role ?? '') === 'ADMIN', [user]);
  // Admins and marketing managers may toggle team curation/draft reviewer defaults.
  const canManageReviewers = useMemo(() => {
    const role = String((user?.team as Record<string, unknown> | undefined)?.role ?? '');
    return canManageReviewerAssignments(role);
  }, [user]);
  const canAddBrandsEffective = useMemo(() => {
    const perms = (user as Record<string, unknown> | undefined)?.permissions as Record<string, unknown> | undefined;
    const flag = perms?.can_add_brands;
    if (flag === undefined) return true;
    return Boolean(flag);
  }, [user]);

  const canEditBrandsEffective = useMemo(() => {
    const perms = (user as Record<string, unknown> | undefined)?.permissions as Record<string, unknown> | undefined;
    const flag = perms?.can_edit_brands;
    if (flag === undefined) return true;
    return Boolean(flag);
  }, [user]);

  const canRenameTeamEffective = useMemo(() => {
    const perms = (user as Record<string, unknown> | undefined)?.permissions as Record<string, unknown> | undefined;
    const flag = perms?.can_rename_team;
    if (flag === undefined) return true;
    return Boolean(flag);
  }, [user]);

  const canInviteMembersEffective = useMemo(() => {
    const perms = (user as Record<string, unknown> | undefined)?.permissions as Record<string, unknown> | undefined;
    const flag = perms?.can_invite_team_members;
    if (flag === undefined) return true;
    return Boolean(flag);
  }, [user]);

  const currentTeamName = useMemo(() => {
    if (currentTeamId) {
      const match = teams.find((t) => t.team_id === currentTeamId);
      if (match?.name) return match.name;
    }
    if (typeof user?.team?.name === 'string' && user.team.name) return user.team.name;
    return 'Team';
  }, [currentTeamId, teams, user?.team?.name]);

  const mapApiBrandToUiBrand = useMemo(() => {
    return (raw: Record<string, unknown>): Brand => {
      const logo =
        typeof raw.logo_url === 'string'
          ? raw.logo_url
          : typeof raw.logo_square_url === 'string'
            ? raw.logo_square_url
            : typeof raw.logo === 'string'
              ? raw.logo
              : '';
      const url = typeof raw.domain === 'string' ? raw.domain : typeof raw.url === 'string' ? raw.url : undefined;
      const hasLogo = typeof raw.has_logo === 'boolean' ? raw.has_logo : Boolean(logo);
      return {
        id: String(raw.brand_id ?? ''),
        name: String(raw.name ?? ''),
        logo,
        url,
        description: typeof raw.description === 'string' ? raw.description : undefined,
        hasLogo,
      };
    };
  }, []);

  const ACCEPTED_BRAND_LOGO_TYPES = useMemo(
    () => ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'],
    []
  );

  const validateBrandLogoFile = (file: File): string | null => {
    if (!ACCEPTED_BRAND_LOGO_TYPES.includes(file.type)) {
      return 'Unsupported file type. Please upload a JPG, PNG, WebP, or GIF.';
    }
    // 10MB, aligned with other upload flows.
    if (file.size > 10 * 1024 * 1024) {
      return 'File is too large. Please upload an image under 10MB.';
    }
    return null;
  };

  const openEditBrandDialog = (brand: Brand) => {
    setEditingBrandId(brand.id);
    setEditBrandName(brand.name ?? '');
    setEditBrandDescription(brand.description ?? '');
    setEditBrandUrl(brand.url ?? '');
    setEditBrandSearchTerm('');
    setEditBrandShowSearchResults(false);
    setEditBrandExternalResults([]);
    // API may return a placeholder `logo_square_url` even when `has_logo=false`.
    // Treat those as "no logo" in the edit modal to avoid rendering the placeholder asset.
    setEditBrandLogoPreviewSafely(brand.hasLogo ? (brand.logo ?? '') : '');
    setEditBrandLogoUpdate({ kind: 'none' });
    setIsEditBrandOpen(true);
    if (editBrandLogoInputRef.current) editBrandLogoInputRef.current.value = '';
  };

  const closeEditBrandDialog = () => {
    if (isUpdatingBrand || isUploadingEditBrandLogo) return;
    setIsEditBrandOpen(false);
    setEditingBrandId(null);
    setEditBrandName('');
    setEditBrandDescription('');
    setEditBrandUrl('');
    setEditBrandSearchTerm('');
    setEditBrandShowSearchResults(false);
    setEditBrandExternalResults([]);
    setEditBrandLogoPreviewSafely('');
    setEditBrandLogoUpdate({ kind: 'none' });
    setIsSearchingEditBrandLogos(false);
    if (editBrandLogoInputRef.current) editBrandLogoInputRef.current.value = '';
  };

  const handleUpdateBrand = async () => {
    if (!currentTeamId) return;
    if (!editingBrandId) return;
    const token = getTokenFromStorage();
    if (!token) {
      logout();
      return;
    }
    if (isUpdatingBrand) return;

    const name = editBrandName.trim();
    if (!name) {
      showError(t('teamPage.brand.edit.errors.nameRequired'));
      return;
    }

    setIsUpdatingBrand(true);
    try {
      if (editBrandLogoUpdate.kind === 'file') {
        setIsUploadingEditBrandLogo(true);
        try {
          const uploaded = await uploadTeamBrandLogo(currentTeamId, editingBrandId, token, editBrandLogoUpdate.file);
          const uploadedUi = mapApiBrandToUiBrand(uploaded as unknown as Record<string, unknown>);
          setBrands((prev) => prev.map((b) => (b.id === uploadedUi.id ? { ...b, ...uploadedUi } : b)));
          setEditBrandLogoPreviewSafely(uploadedUi.logo ?? '');
          setEditBrandLogoUpdate({ kind: 'none' });
          if (editBrandLogoInputRef.current) editBrandLogoInputRef.current.value = '';
        } catch {
          showError(t('teamPage.brand.edit.errors.logoUploadFailed'));
          return;
        } finally {
          setIsUploadingEditBrandLogo(false);
        }
      }

      const body: Parameters<typeof updateTeamBrand>[3] = {
        name,
        description: editBrandDescription.trim() ? editBrandDescription.trim() : null,
        url: editBrandUrl.trim() ? editBrandUrl.trim() : null,
      };

      if (editBrandLogoUpdate.kind === 'brandfetch') {
        body.logo_url = editBrandLogoUpdate.logoUrl;
        body.logo_cloudinary_id = null;
      }

      const updated = await updateTeamBrand(currentTeamId, editingBrandId, token, {
        ...body,
      });
      const raw = updated as unknown as Record<string, unknown>;
      const ui = mapApiBrandToUiBrand(raw);
      setBrands((prev) => prev.map((b) => (b.id === ui.id ? { ...b, ...ui } : b)));
      closeEditBrandDialog();
    } catch {
      // Keep UI as-is on error.
    } finally {
      setIsUpdatingBrand(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    async function run() {
      const token = getTokenFromStorage();
      if (!token) {
        logout();
        return;
      }
      setIsLoadingTeams(true);
      try {
        const res = await getTeamsSearch(token);
        if (cancelled) return;
        const rows = Array.isArray(res?.teams) ? res.teams : [];
        setTeams(rows);
        const preferred = currentUserTeamId ?? rows[0]?.team_id ?? null;
        setCurrentTeamId(preferred);
      } catch {
        if (!cancelled) setCurrentTeamId(currentUserTeamId);
      } finally {
        if (!cancelled) setIsLoadingTeams(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
    // currentUserTeamId is intentionally the only dependency.
  }, [currentUserTeamId, logout]);

  const handleSelectTeam = async (teamId: string) => {
    if (!teamId) return;
    if (teamId === currentTeamId) {
      setIsTeamPickerOpen(false);
      return;
    }
    const token = getTokenFromStorage();
    if (!token) {
      logout();
      return;
    }
    if (isSwitchingTeam) return;

    setIsSwitchingTeam(true);
    try {
      const { token: newToken, user: updatedUser } = await selectClientTeam(teamId, token);
      updateAuthInStorage({ token: newToken, user: updatedUser });
      setUser?.(updatedUser as never);
      setCurrentTeamId(teamId);
      setIsTeamPickerOpen(false);
    } catch {
      // Keep UI as-is on error.
    } finally {
      setIsSwitchingTeam(false);
    }
  };

  const openEditTeamNameDialog = () => {
    setEditTeamName(currentTeamName);
    setIsEditTeamNameOpen(true);
  };

  const handleUpdateTeamName = async () => {
    if (!currentTeamId) return;
    const token = getTokenFromStorage();
    if (!token) {
      logout();
      return;
    }
    if (isUpdatingTeamName) return;
    const name = editTeamName.trim();
    if (!name) return;

    setIsUpdatingTeamName(true);
    try {
      const updated = await updateTeam(currentTeamId, token, { name });
      const nextName = typeof updated?.name === 'string' && updated.name.trim() ? updated.name.trim() : name;

      setTeams((prev) => prev.map((t) => (t.team_id === currentTeamId ? { ...t, name: nextName } : t)));

      if (user?.team && user.team.team_id === currentTeamId) {
        const updatedUser = {
          ...(user as Record<string, unknown>),
          team: { ...(user.team as Record<string, unknown>), team_id: currentTeamId, name: nextName },
        };
        updateAuthInStorage({ user: updatedUser as never });
        setUser?.(updatedUser as never);
      }

      setIsEditTeamNameOpen(false);
    } catch {
      // Keep UI as-is on error.
    } finally {
      setIsUpdatingTeamName(false);
    }
  };

  useLayoutEffect(() => {
    if (!isTeamPickerOpen) {
      setTeamPickerMenuStyle(null);
      return;
    }

    function recompute() {
      const trigger = teamPickerTriggerRef.current;
      const menu = teamPickerMenuRef.current;
      if (!trigger || !menu) return;
      const rect = trigger.getBoundingClientRect();

      const desiredWidth = Math.max(Math.min(360, rect.width), 260);
      const menuHeight = menu.offsetHeight || 260;
      const margin = 8;

      let left = rect.right - desiredWidth;
      left = Math.max(margin, Math.min(left, window.innerWidth - desiredWidth - margin));

      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < menuHeight + margin;
      const top = openUp
        ? Math.max(margin, rect.top - menuHeight - margin)
        : Math.min(window.innerHeight - menuHeight - margin, rect.bottom + margin);

      setTeamPickerMenuStyle({
        position: 'fixed',
        top,
        left,
        width: desiredWidth,
        maxHeight: Math.max(160, window.innerHeight - margin * 2),
        overflowY: 'auto',
        zIndex: 50,
      });
    }

    recompute();
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, true);
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, true);
    };
  }, [isTeamPickerOpen]);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!currentTeamId) return;
      const token = getTokenFromStorage();
      if (!token) {
        logout();
        return;
      }
      setIsLoadingTeamData(true);
      try {
        const [membersRes, brandsRes] = await Promise.all([
          getTeamMembersSearch(currentTeamId, token),
          getTeamBrandsSearch(currentTeamId, token),
        ]);
        if (cancelled) return;

        const apiMembers = Array.isArray(membersRes?.members) ? membersRes.members : [];
        const apiBrands = Array.isArray(brandsRes?.brands) ? brandsRes.brands : [];

        setMembers(
          apiMembers.map((m) => {
            const raw = m as Record<string, unknown>;
            const avatar =
              typeof raw.mugshot_url === 'string'
                ? raw.mugshot_url
                : typeof raw.avatar_url === 'string'
                  ? raw.avatar_url
                  : typeof raw.picture_url === 'string'
                    ? raw.picture_url
                    : null;
            return {
              id: String(m.user_id ?? ''),
              name: String(m.name ?? ''),
              email: String(m.email ?? ''),
              role: isTeamMemberRole(String(m.role ?? '')) ? (String(m.role ?? '') as TeamMemberRole) : 'EXTERNAL_PARTNER',
              status: typeof raw.status === 'string' ? raw.status : undefined,
              dateInvited: typeof raw.date_invited === 'string' ? raw.date_invited : undefined,
              isCurrentUser: String(m.user_id ?? '') === currentUserId,
              avatar,
              brandSettings: mapBrandSettingsFromApi(raw.brand_settings),
              // API omits flags for legacy members; treat missing as opted-in (true).
              isCurationReviewer: raw.is_curation_reviewer !== false,
              isDraftReviewer: raw.is_draft_reviewer !== false,
            };
          })
        );

        setBrands(
          apiBrands
            .filter((b) => (b as { is_archived?: boolean }).is_archived !== true)
            .map((b) => {
              const raw = b as Record<string, unknown>;
              return mapApiBrandToUiBrand(raw);
            })
        );
      } catch {
        if (cancelled) return;
        setMembers([]);
        setBrands([]);
      } finally {
        if (!cancelled) setIsLoadingTeamData(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [currentTeamId, currentUserId, logout]);

  const searchBrandsExternal = async (term: string): Promise<ExternalBrand[]> => {
    const cleaned = term.trim();
    if (!cleaned) return [];

    const clientId = typeof import.meta.env.VITE_BRANDFETCH_CLIENT_ID === 'string' ? import.meta.env.VITE_BRANDFETCH_CLIENT_ID.trim() : '';
    const apiKey = typeof import.meta.env.VITE_BRANDFETCH_API_KEY === 'string' ? import.meta.env.VITE_BRANDFETCH_API_KEY.trim() : '';

    // If Brandfetch isn't configured, fall back to a single manual-like entry (same spirit as web-app).
    if (!clientId) {
      return [{ name: cleaned, domain: `${cleaned}.com` }];
    }

    try {
      const searchResp = await fetch(`https://api.brandfetch.io/v2/search/${encodeURIComponent(cleaned)}?c=${encodeURIComponent(clientId)}`);
      const searchJson = await searchResp.json().catch(() => null);
      if (!searchResp.ok || !Array.isArray(searchJson)) {
        return [{ name: cleaned, domain: `${cleaned}.com` }];
      }

      const top = searchJson.slice(0, 8).map((b: { domain?: unknown; name?: unknown; icon?: unknown }) => ({
        domain: typeof b.domain === 'string' ? b.domain : '',
        name: typeof b.name === 'string' ? b.name : cleaned,
        icon: typeof b.icon === 'string' ? b.icon : undefined,
      })).filter((b: { domain: string }) => Boolean(b.domain));

      const results: ExternalBrand[] = [];
      await Promise.all(
        top.map(async (b: { domain: string; name: string; icon?: string }) => {
          if (!apiKey) {
            results.push({ name: b.name, domain: b.domain, logo: b.icon });
            return;
          }
          try {
            const detailsResp = await fetch(`https://api.brandfetch.io/v2/brands/${encodeURIComponent(b.domain)}`, {
              headers: { Authorization: `Bearer ${apiKey}` },
            });
            const detailsJson = await detailsResp.json().catch(() => null);
            if (detailsResp.ok && detailsJson && typeof detailsJson === 'object') {
              const d = detailsJson as Record<string, unknown>;
              const name = typeof d.name === 'string' ? d.name : b.name;
              const icon = typeof d.icon === 'string' ? d.icon : b.icon;
              const logos = Array.isArray(d.logos) ? (d.logos as Array<Record<string, unknown>>) : [];
              const maybeLogo = icon || (typeof logos?.[0]?.image === 'string' ? String(logos[0].image) : undefined);
              results.push({ name, domain: b.domain, logo: maybeLogo });
              return;
            }
            results.push({ name: b.name, domain: b.domain, logo: b.icon });
          } catch {
            results.push({ name: b.name, domain: b.domain, logo: b.icon });
          }
        })
      );

      return results.length > 0 ? results : [{ name: cleaned, domain: `${cleaned}.com` }];
    } catch {
      return [{ name: cleaned, domain: `${cleaned}.com` }];
    }
  };

  const handleCreateBrand = async (payload: { name: string; url?: string; logo_url?: string }) => {
    if (!currentTeamId) return;
    const token = getTokenFromStorage();
    if (!token) {
      logout();
      return;
    }
    if (isCreatingBrand) return;

    const name = payload.name.trim();
    if (!name) return;

    setIsCreatingBrand(true);
    try {
      const created = await createTeamBrand(currentTeamId, token, {
        name,
        url: payload.url,
        logo_url: payload.logo_url,
      });
      const createdRaw = created as unknown as Record<string, unknown>;
      const createdUi = mapApiBrandToUiBrand(createdRaw);
      setBrands((prev) => {
        if (prev.some((b) => b.id === createdUi.id)) return prev;
        return [...prev, createdUi];
      });

      setIsAddBrandOpen(false);
      setBrandCreateName('');
      setBrandSearchTerm('');
      setBrandsExternal([]);
      setShowBrandSearchResults(false);
    } catch {
      // Keep UI as-is on error.
    } finally {
      setIsCreatingBrand(false);
    }
  };

  const handleRemoveBrand = (id: string) => {
    setBrands((prev) => prev.filter((b) => b.id !== id));
  };

  const handleArchiveBrand = async () => {
    if (!deleteBrandId) return;
    if (!currentTeamId) return;
    const token = getTokenFromStorage();
    if (!token) {
      logout();
      return;
    }
    if (isArchivingBrand) return;

    setIsArchivingBrand(true);
    try {
      await archiveTeamBrand(currentTeamId, deleteBrandId, token);
      handleRemoveBrand(deleteBrandId);
      setDeleteBrandId(null);
    } catch {
      // Keep UI as-is on error.
    } finally {
      setIsArchivingBrand(false);
    }
  };

  const handleRemoveMember = async (id: string) => {
    if (!currentTeamId) return;
    const token = getTokenFromStorage();
    if (!token) {
      logout();
      return;
    }
    if (removingMemberId) return;
    setRemovingMemberId(id);
    try {
      await removeTeamMember(currentTeamId, id, token);
      setMembers((prev) => prev.filter((m) => m.id !== id));

      if (id === currentUserId) {
        const refreshed = await getCurrentUser(token);
        updateAuthInStorage({ token, user: refreshed });
        setUser?.(refreshed);
      }
    } catch {
      // Keep UI as-is on error.
    } finally {
      setRemovingMemberId(null);
    }
  };

  const handleRoleChange = async (id: string, role: TeamMemberRole) => {
    if (!currentTeamId) return;
    const token = getTokenFromStorage();
    if (!token) {
      logout();
      return;
    }
    if (updatingMemberId) return;
    const prevRole = members.find((m) => m.id === id)?.role ?? 'EXTERNAL_PARTNER';
    setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, role } : m)));
    setUpdatingMemberId(id);
    try {
      await updateTeamMember(currentTeamId, id, token, { role });
    } catch {
      setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, role: prevRole } : m)));
    } finally {
      setUpdatingMemberId(null);
    }
  };

  /**
   * Persist a team-default curation/draft reviewer flag with optimistic UI.
   * Only active members can be toggled; rolls back both flags for that row on error.
   */
  const handleReviewerFlagChange = async (
    id: string,
    flag: 'isCurationReviewer' | 'isDraftReviewer',
    value: boolean,
  ) => {
    if (!currentTeamId) return;
    // Invites are not reviewers until they accept and become MEMBER.
    if ((members.find((m) => m.id === id)?.status ?? 'MEMBER') !== 'MEMBER') return;
    const token = getTokenFromStorage();
    if (!token) {
      logout();
      return;
    }
    if (updatingMemberId) return;
    const prev = members.find((m) => m.id === id);
    if (!prev) return;
    // 1. Optimistic local toggle
    setMembers((prevMembers) =>
      prevMembers.map((m) => (m.id === id ? { ...m, [flag]: value } : m)),
    );
    setUpdatingMemberId(id);
    try {
      // 2. PATCH only the changed flag (role stays untouched)
      await updateTeamMember(currentTeamId, id, token, {
        is_curation_reviewer: flag === 'isCurationReviewer' ? value : undefined,
        is_draft_reviewer: flag === 'isDraftReviewer' ? value : undefined,
      });
    } catch {
      setMembers((prevMembers) =>
        prevMembers.map((m) =>
          m.id === id
            ? {
                ...m,
                isCurationReviewer: prev.isCurationReviewer,
                isDraftReviewer: prev.isDraftReviewer,
              }
            : m,
        ),
      );
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const handleLeaveTeam = async () => {
    if (!currentTeamId) return;
    if (!currentUserId) return;
    const token = getTokenFromStorage();
    if (!token) {
      logout();
      return;
    }
    if (isLeavingTeam) return;

    setIsLeavingTeam(true);
    try {
      await removeTeamMember(currentTeamId, currentUserId, token);
      const refreshed = await getCurrentUser(token);
      updateAuthInStorage({ token, user: refreshed });
      setUser?.(refreshed);
      window.location.href = '/';
    } catch {
      // Keep UI as-is on error.
    } finally {
      setIsLeavingTeam(false);
    }
  };

  const handleInviteMember = async () => {
    if (!currentTeamId) return;
    const token = getTokenFromStorage();
    if (!token) {
      logout();
      return;
    }
    if (isInvitingMember) return;

    const name = inviteMemberName.trim();
    const email = inviteMemberEmail.trim();
    const role = inviteMemberRole;
    if (!name || !email || !role) return;

    setIsInvitingMember(true);
    try {
      const created = await inviteTeamMember(currentTeamId, token, { name, email, role });
      const raw = created as unknown as Record<string, unknown>;
      const avatar =
        typeof raw.mugshot_url === 'string'
          ? raw.mugshot_url
          : typeof raw.avatar_url === 'string'
            ? raw.avatar_url
            : typeof raw.picture_url === 'string'
              ? raw.picture_url
              : null;

      setMembers((prev) => [
        ...prev,
        {
          id: String(raw.user_id ?? ''),
          name: String(raw.name ?? name),
          email: String(raw.email ?? email),
          role: isTeamMemberRole(String(raw.role ?? role)) ? (String(raw.role ?? role) as TeamMemberRole) : role,
          status: typeof raw.status === 'string' ? raw.status : 'PENDING',
          dateInvited: typeof raw.date_invited === 'string' ? raw.date_invited : undefined,
          isCurrentUser: false,
          avatar,
          brandSettings: mapBrandSettingsFromApi(raw.brand_settings),
          // Same default as list mapping: missing API flags mean opted-in.
          isCurationReviewer: raw.is_curation_reviewer !== false,
          isDraftReviewer: raw.is_draft_reviewer !== false,
        },
      ]);

      setIsAddMemberOpen(false);
      setInviteMemberName('');
      setInviteMemberEmail('');
      setInviteMemberRole('');
    } catch {
      // Keep UI as-is on error.
    } finally {
      setIsInvitingMember(false);
    }
  };

  const handleRemindMember = async (member: Member) => {
    if (!currentTeamId) return;
    const token = getTokenFromStorage();
    if (!token) {
      logout();
      return;
    }
    if (remindingMemberId) return;

    setRemindingMemberId(member.id);
    try {
      const res = await inviteTeamMember(currentTeamId, token, {
        name: member.name,
        email: member.email,
        role: member.role,
      });
      const raw = res as unknown as Record<string, unknown>;
      const status = typeof raw.status === 'string' ? raw.status : member.status;
      const dateInvited = typeof raw.date_invited === 'string' ? raw.date_invited : member.dateInvited;
      setMembers((prev) =>
        prev.map((m) =>
          m.id === member.id
            ? {
                ...m,
                status,
                dateInvited,
                brandSettings: mapBrandSettingsFromApi(raw.brand_settings) ?? m.brandSettings,
              }
            : m
        )
      );
    } catch {
      // Keep UI as-is on error.
    } finally {
      setRemindingMemberId(null);
    }
  };

  return (
    <div className="max-w-[1600px] mx-auto p-6 lg:p-8 bg-[#f8f9fa] min-h-screen">
      {/* ── Branded Page Header ── */}
      <PageHeader
        category={t('teamPage.header.category')}
        title={
          <span className="inline-flex items-center gap-2">
            <span>{currentTeamName}</span>
            {canRenameTeamEffective && currentTeamName ? (
              <button
                type="button"
                onClick={openEditTeamNameDialog}
                disabled={isLoadingTeams || isSwitchingTeam}
                className="w-8 h-8 rounded-lg inline-flex items-center justify-center text-[#be43ff] hover:bg-[#be43ff]/5 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                aria-label={t('teamPage.team.rename.triggerLabel')}
                title={t('teamPage.team.rename.triggerLabel')}
              >
                <Pencil className="w-4 h-4" />
              </button>
            ) : null}
          </span>
        }
        subtitle={t('teamPage.header.subtitle')}
        actions={
          <div className="relative">
            <button
              ref={teamPickerTriggerRef}
              type="button"
              onClick={() => setIsTeamPickerOpen((o) => !o)}
              disabled={isLoadingTeams || isSwitchingTeam || teams.length === 0}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm text-[#344054] transition-all hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#be43ff]/30 focus:border-[#be43ff] disabled:opacity-60 disabled:cursor-not-allowed max-w-[360px]"
              style={{ fontWeight: 600 }}
              aria-label="Select team"
              aria-haspopup="menu"
              aria-expanded={isTeamPickerOpen}
            >
              <span className="truncate">{currentTeamName}</span>
              {isSwitchingTeam ? (
                <Loader2 className="w-4 h-4 text-[#be43ff] animate-spin" aria-hidden />
              ) : (
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isTeamPickerOpen ? 'rotate-180' : ''}`} />
              )}
            </button>
            {isTeamPickerOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setIsTeamPickerOpen(false)} />
                <div
                  ref={teamPickerMenuRef}
                  style={teamPickerMenuStyle ?? { position: 'fixed', top: 0, left: 0, zIndex: 50, width: 320 }}
                  className="bg-white rounded-2xl border border-gray-100 shadow-lg shadow-black/5 overflow-hidden py-1"
                >
                  {teamsSortedByName.map((t) => {
                    const isActive = t.team_id === currentTeamId;
                    return (
                      <button
                        key={t.team_id}
                        type="button"
                        onClick={() => handleSelectTeam(t.team_id)}
                        disabled={isSwitchingTeam}
                        className={`w-full text-left px-4 py-3 text-sm transition-colors flex items-center justify-between gap-3 ${
                          isActive ? 'text-[#be43ff] bg-[#be43ff]/5' : 'text-[#344054] hover:bg-gray-50'
                        } disabled:opacity-60 disabled:cursor-not-allowed`}
                      >
                        <span className="truncate" style={{ fontWeight: 600 }}>
                          {t.name}
                        </span>
                        {isActive ? <Check className="w-4 h-4 text-[#be43ff] flex-shrink-0" /> : null}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        }
      />

      {/* Edit Team Name Dialog */}
      <Dialog
        open={isEditTeamNameOpen}
        onOpenChange={(open) => {
          if (!open && !isUpdatingTeamName) {
            setIsEditTeamNameOpen(false);
            setEditTeamName(currentTeamName);
          } else {
            setIsEditTeamNameOpen(open);
          }
        }}
      >
        <DialogContent className="max-w-[760px] rounded-2xl border border-gray-100 bg-white p-0 shadow-[0_16px_48px_rgba(16,24,40,0.16)]">
          <div className="px-10 py-8">
            <div className="space-y-6">
              <div>
                <div className="text-[12px] text-[#667085] mb-2" style={{ fontWeight: 600 }}>
                  {t('teamPage.team.rename.fields.name')} <span className="text-[#ff5635]">*</span>
                </div>
                <Input
                  value={editTeamName}
                  onChange={(e) => setEditTeamName(e.target.value)}
                  disabled={isUpdatingTeamName}
                  aria-label={t('teamPage.team.rename.fields.name')}
                  className="h-12 rounded-xl text-base"
                />
              </div>
              <Button
                type="button"
                onClick={() => void handleUpdateTeamName()}
                disabled={isUpdatingTeamName || editTeamName.trim().length === 0}
                className="h-12 w-full rounded-full bg-[#2e024c] hover:bg-[#3a0d5e] text-white font-semibold tracking-[0.12em] text-[12px] disabled:opacity-100 disabled:bg-[#d0d5dd] disabled:hover:bg-[#d0d5dd]"
              >
                {isUpdatingTeamName ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                    {t('teamPage.team.rename.actions.save')}
                  </>
                ) : (
                  t('teamPage.team.rename.actions.save')
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─────────────────────── Brands Section ─────────────────────── */}
      <div className="mb-12">
        <div className="flex items-center justify-between mb-6">
          <SectionTitle title="Brands" />
          <button
            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-xl bg-[#2e024c] text-white text-sm hover:bg-[#3a0d5e] transition-colors active:scale-[0.97] disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ fontWeight: 600, letterSpacing: '0.04em' }}
            onClick={() => setIsAddBrandOpen(true)}
            disabled={!canAddBrandsEffective || !currentTeamId || isLoadingTeams || isLoadingTeamData}
          >
            ADD BRAND
          </button>
        </div>

        <div className="space-y-3">
          {(isLoadingTeams || isLoadingTeamData) && (
            <div className="flex items-center justify-center py-10 text-[#98a2b3] text-sm">
              <Loader2 className="w-5 h-5 text-[#be43ff] animate-spin mr-2" aria-hidden />
              Loading team…
            </div>
          )}
          {brands.map((brand) => (
            <div
              key={brand.id}
              className="flex items-center gap-4 px-5 py-4 bg-white rounded-2xl border border-gray-100 hover:border-gray-200 transition-all group"
            >
              {/* Brand Logo */}
              <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-100 shrink-0 border border-gray-100">
                <ImageWithFallback
                  src={brand.logo}
                  alt={brand.name}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Brand Info */}
              <div className="flex-1 min-w-0">
                <p className="text-[#101828] text-sm truncate" style={{ fontWeight: 600 }}>
                  {brand.name}
                </p>
                {brand.url && (
                  <a
                    href={normalizeExternalUrl(brand.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#be43ff] text-xs hover:underline inline-flex items-center gap-1 mt-0.5"
                  >
                    {brand.url.replace(/^https?:\/\//i, '')}
                    <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => setDeleteBrandId(brand.id)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-[#ff5635]/70 hover:text-[#ff5635] hover:bg-[#ff5635]/5 transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Remove brand"
                  disabled={!canEditBrandsEffective}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => openEditBrandDialog(brand)}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg border border-[#be43ff]/30 text-[#be43ff] text-xs hover:bg-[#be43ff]/5 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ fontWeight: 600, letterSpacing: '0.04em' }}
                  disabled={!canEditBrandsEffective}
                >
                  EDIT
                </button>
              </div>
            </div>
          ))}

          {!isLoadingTeams && !isLoadingTeamData && brands.length === 0 && (
            <div className="text-center py-12 text-[#98a2b3] text-sm">
              No brands added yet. Click "ADD BRAND" to get started.
            </div>
          )}
        </div>
      </div>

      {/* Add Brand Dialog */}
      <Dialog
        open={isAddBrandOpen}
        onOpenChange={(open) => {
          setIsAddBrandOpen(open);
          if (!open) {
            setBrandCreateName('');
            setBrandSearchTerm('');
            setBrandsExternal([]);
            setShowBrandSearchResults(false);
          }
        }}
      >
        <DialogContent className="max-w-[820px] rounded-2xl border border-gray-100 bg-white p-0 shadow-[0_16px_48px_rgba(16,24,40,0.16)]">
          <div className="px-10 py-8">
            <div className="space-y-6">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void handleCreateBrand({ name: brandCreateName });
                }}
                className="grid grid-cols-[1fr_170px] items-center gap-6 w-full"
              >
                <div className="min-w-0">
                  <Input
                    value={brandCreateName}
                    onChange={(e) => setBrandCreateName(e.target.value)}
                    placeholder="Name"
                    disabled={isCreatingBrand}
                    className="h-12 rounded-xl text-base"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isCreatingBrand || brandCreateName.trim().length === 0}
                  className="h-12 w-[170px] rounded-full bg-[#2e024c] hover:bg-[#3a0d5e] text-white font-semibold tracking-[0.12em] text-[12px] disabled:opacity-100 disabled:bg-[#d0d5dd] disabled:hover:bg-[#d0d5dd]"
                >
                  {isCreatingBrand ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                      CREATE BRAND
                    </>
                  ) : (
                    'CREATE BRAND'
                  )}
                </Button>
              </form>

              <div className="flex items-center gap-6">
                <div className="h-px bg-gray-200 flex-1" />
                <div className="text-[12px] tracking-[0.14em] text-[#667085]" style={{ fontWeight: 600 }}>
                  OR USE EXISTING BRAND
                </div>
                <div className="h-px bg-gray-200 flex-1" />
              </div>

              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const term = brandSearchTerm.trim();
                  if (!term) {
                    setShowBrandSearchResults(false);
                    setBrandsExternal([]);
                    return;
                  }
                  if (isSearchingBrands) return;
                  setBrandsExternal([]);
                  setShowBrandSearchResults(true);
                  setIsSearchingBrands(true);
                  try {
                    const rows = await searchBrandsExternal(term);
                    setBrandsExternal(rows);
                  } finally {
                    setIsSearchingBrands(false);
                  }
                }}
                className="grid grid-cols-[1fr_170px] items-center gap-6 w-full"
              >
                <div className="relative min-w-0">
                  <Input
                    value={brandSearchTerm}
                    onChange={(e) => {
                      setBrandSearchTerm(e.target.value);
                      setShowBrandSearchResults(false);
                      setBrandsExternal([]);
                    }}
                    placeholder="Brand-name"
                    disabled={isCreatingBrand}
                    className="h-12 rounded-xl text-base"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        // allow form submit
                      }
                    }}
                  />
                  {!isSearchingBrands && !isCreatingBrand && showBrandSearchResults && brandsExternal.length > 0 ? (
                    <div className="absolute left-0 right-0 mt-2 bg-white rounded-2xl border border-gray-100 shadow-lg shadow-black/5 overflow-hidden z-10">
                      {brandsExternal.map((b) => (
                        <button
                          key={`${b.domain}-${b.name}`}
                          type="button"
                          onClick={() => {
                            void handleCreateBrand({
                              name: b.name,
                              url: b.domain,
                              logo_url: b.logo,
                            });
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left"
                        >
                          <div className="w-9 h-9 rounded-xl overflow-hidden bg-gray-100 border border-gray-100 flex items-center justify-center shrink-0">
                            {b.logo ? (
                              <ImageWithFallback src={b.logo} alt={b.name} className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-[10px] text-[#98a2b3]" style={{ fontWeight: 700 }}>
                                {b.name.slice(0, 1).toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm text-[#101828] truncate" style={{ fontWeight: 600 }}>
                              {b.name}
                            </div>
                            <div className="text-xs text-[#667085] truncate">{b.domain}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
                <Button
                  type="submit"
                  disabled={isCreatingBrand || brandSearchTerm.trim().length === 0}
                  className="h-12 w-[170px] rounded-full bg-[#2e024c] hover:bg-[#3a0d5e] text-white font-semibold tracking-[0.12em] text-[12px] disabled:opacity-100 disabled:bg-[#d0d5dd] disabled:hover:bg-[#d0d5dd]"
                >
                  {isSearchingBrands ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                      SEARCH
                    </>
                  ) : (
                    'SEARCH'
                  )}
                </Button>
              </form>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Brand Dialog */}
      <Dialog
        open={isEditBrandOpen}
        onOpenChange={(open) => {
          if (open) return;
          closeEditBrandDialog();
        }}
      >
        <DialogContent className="max-w-[820px] rounded-2xl border border-gray-100 bg-white p-0 shadow-[0_16px_48px_rgba(16,24,40,0.16)]">
          <div className="px-10 py-8">
            <div className="space-y-6">
              <div className="flex items-center gap-6">
                <div
                  className="relative shrink-0 overflow-visible inline-flex"
                  style={{ width: 60, height: 60, minWidth: 60, minHeight: 60, maxWidth: 60, maxHeight: 60, flex: '0 0 60px' }}
                >
                  <div
                    className="rounded-full overflow-hidden bg-black"
                    style={{ width: 60, height: 60, minWidth: 60, minHeight: 60, maxWidth: 60, maxHeight: 60 }}
                  >
                    {isUploadingEditBrandLogo ? (
                      <div className="w-full h-full flex items-center justify-center">
                        <Loader2 className="w-5 h-5 text-white animate-spin" aria-hidden />
                      </div>
                    ) : editBrandLogoPreview ? (
                      <ImageWithFallback
                        src={editBrandLogoPreview}
                        alt={editBrandName}
                        width={60}
                        height={60}
                        className="block object-cover"
                        style={{ width: 60, height: 60, minWidth: 60, minHeight: 60, maxWidth: 60, maxHeight: 60 }}
                      />
                    ) : (
                      <div
                        className="w-full h-full flex items-center justify-center text-white text-[12px]"
                        style={{ fontWeight: 700, letterSpacing: '0.08em' }}
                      >
                        {(editBrandName.trim() || 'B').slice(0, 2).toUpperCase()}
                      </div>
                    )}
                  </div>

                  <label
                    className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center cursor-pointer shadow-sm"
                    title={t('teamPage.brand.edit.actions.uploadLogo')}
                  >
                    <Upload className="w-4 h-4 text-[#be43ff]" />
                    <input
                      ref={editBrandLogoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={isUpdatingBrand || isUploadingEditBrandLogo}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const err = validateBrandLogoFile(file);
                        if (err) {
                          showError(err);
                          if (editBrandLogoInputRef.current) editBrandLogoInputRef.current.value = '';
                          return;
                        }

                        const localPreview = URL.createObjectURL(file);
                        setEditBrandLogoPreviewSafely(localPreview);
                        setEditBrandLogoUpdate({ kind: 'file', file });
                      }}
                    />
                  </label>
                </div>

                <form
                  className="flex-1 grid grid-cols-[1fr_170px] items-center gap-6"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const term = editBrandSearchTerm.trim();
                    if (!term) {
                      setEditBrandShowSearchResults(false);
                      setEditBrandExternalResults([]);
                      return;
                    }
                    if (isSearchingEditBrandLogos) return;
                    setEditBrandExternalResults([]);
                    setEditBrandShowSearchResults(true);
                    setIsSearchingEditBrandLogos(true);
                    try {
                      const rows = await searchBrandsExternal(term);
                      setEditBrandExternalResults(rows);
                    } finally {
                      setIsSearchingEditBrandLogos(false);
                    }
                  }}
                >
                  <div className="relative min-w-0">
                    <Input
                      value={editBrandSearchTerm}
                      onChange={(e) => {
                        setEditBrandSearchTerm(e.target.value);
                        setEditBrandShowSearchResults(false);
                        setEditBrandExternalResults([]);
                      }}
                      placeholder={t('teamPage.brand.edit.search.placeholder')}
                      disabled={isUpdatingBrand || isUploadingEditBrandLogo}
                      className="h-12 rounded-xl text-base"
                    />
                    {!isSearchingEditBrandLogos &&
                    !isUpdatingBrand &&
                    !isUploadingEditBrandLogo &&
                    editBrandShowSearchResults &&
                    editBrandExternalResults.length > 0 ? (
                      <div className="absolute left-0 right-0 mt-2 bg-white rounded-2xl border border-gray-100 shadow-lg shadow-black/5 overflow-hidden z-10">
                        {editBrandExternalResults.map((b) => (
                          <button
                            key={`${b.domain}-${b.name}`}
                            type="button"
                            onClick={() => {
                              const logo = b.logo ?? '';
                              if (!logo) return;
                              setEditBrandLogoPreviewSafely(logo);
                              setEditBrandLogoUpdate({ kind: 'brandfetch', logoUrl: logo });
                              setEditBrandSearchTerm('');
                              setEditBrandShowSearchResults(false);
                              setEditBrandExternalResults([]);
                              if (editBrandLogoInputRef.current) editBrandLogoInputRef.current.value = '';
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left"
                          >
                            <div className="w-9 h-9 rounded-xl overflow-hidden bg-gray-100 border border-gray-100 flex items-center justify-center shrink-0">
                              {b.logo ? (
                                <ImageWithFallback src={b.logo} alt={b.name} className="w-full h-full object-cover" />
                              ) : (
                                <span className="text-[10px] text-[#98a2b3]" style={{ fontWeight: 700 }}>
                                  {b.name.slice(0, 1).toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm text-[#101828] truncate" style={{ fontWeight: 600 }}>
                                {b.name}
                              </div>
                              <div className="text-xs text-[#667085] truncate">{b.domain}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>

                  <Button
                    type="submit"
                    disabled={isUpdatingBrand || isUploadingEditBrandLogo || editBrandSearchTerm.trim().length === 0}
                    className="h-12 w-[170px] rounded-full bg-[#2e024c] hover:bg-[#3a0d5e] text-white font-semibold tracking-[0.12em] text-[12px] disabled:opacity-100 disabled:bg-[#d0d5dd] disabled:hover:bg-[#d0d5dd]"
                  >
                    {isSearchingEditBrandLogos ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                        {t('teamPage.brand.edit.actions.search')}
                      </>
                    ) : (
                      t('teamPage.brand.edit.actions.search')
                    )}
                  </Button>
                </form>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="text-[12px] text-[#667085] mb-2" style={{ fontWeight: 600 }}>
                    {t('teamPage.brand.edit.fields.name')} <span className="text-[#ff5635]">*</span>
                  </div>
                  <Input
                    value={editBrandName}
                    onChange={(e) => setEditBrandName(e.target.value)}
                    disabled={isUpdatingBrand || isUploadingEditBrandLogo}
                    className="h-12 rounded-xl text-base"
                  />
                </div>

                <div>
                  <div className="text-[12px] text-[#667085] mb-2" style={{ fontWeight: 600 }}>
                    {t('teamPage.brand.edit.fields.description')}
                  </div>
                  <Input
                    value={editBrandDescription}
                    onChange={(e) => setEditBrandDescription(e.target.value)}
                    disabled={isUpdatingBrand || isUploadingEditBrandLogo}
                    className="h-12 rounded-xl text-base"
                  />
                </div>

                <div>
                  <div className="text-[12px] text-[#667085] mb-2" style={{ fontWeight: 600 }}>
                    {t('teamPage.brand.edit.fields.url')}
                  </div>
                  <Input
                    value={editBrandUrl}
                    onChange={(e) => setEditBrandUrl(e.target.value)}
                    disabled={isUpdatingBrand || isUploadingEditBrandLogo}
                    className="h-12 rounded-xl text-base"
                  />
                </div>
              </div>

              <Button
                type="button"
                onClick={() => void handleUpdateBrand()}
                disabled={isUpdatingBrand || isUploadingEditBrandLogo || editBrandName.trim().length === 0}
                className="h-12 w-full rounded-full bg-[#2e024c] hover:bg-[#3a0d5e] text-white font-semibold tracking-[0.12em] text-[12px] disabled:opacity-100 disabled:bg-[#d0d5dd] disabled:hover:bg-[#d0d5dd]"
              >
                {isUpdatingBrand ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                    {t('teamPage.brand.edit.actions.save')}
                  </>
                ) : (
                  t('teamPage.brand.edit.actions.save')
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Brand Confirmation */}
      <AlertDialog
        open={deleteBrandId !== null}
        onOpenChange={(open) => {
          if (!open && !isArchivingBrand) setDeleteBrandId(null);
        }}
      >
        <AlertDialogContent
          className="max-w-[400px] rounded-[24px] border border-gray-100 bg-white p-0"
          style={{ boxShadow: '0 24px 80px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)' }}
        >
          <div className="p-8 pt-8 text-center">
            <div className="w-14 h-14 rounded-2xl bg-[#be43ff]/10 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-7 h-7 text-[#be43ff]" aria-hidden />
            </div>
            <h3 className="text-[#101828] text-lg mb-2" style={{ fontWeight: 600 }}>
              Do you really want to remove this brand?
            </h3>
            <p className="text-[#667085] text-sm mb-6 max-w-[320px] mx-auto">
              This action will archive the brand for the selected team.
            </p>
            <div className="flex gap-3 justify-center">
              <AlertDialogCancel
                className="px-6 py-2.5 rounded-xl border-2 border-[#ff5635] text-sm text-[#ff5635] bg-white hover:bg-[#ff5635]/5 transition-all active:scale-[0.97]"
                style={{ fontWeight: 600 }}
                disabled={isArchivingBrand}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                className="px-6 py-2.5 rounded-xl bg-[#be43ff] text-white text-sm hover:bg-[#a830e0] transition-all active:scale-[0.97]"
                style={{ fontWeight: 600 }}
                onClick={() => void handleArchiveBrand()}
                disabled={isArchivingBrand}
              >
                {isArchivingBrand ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                    Yes
                  </>
                ) : (
                  'Yes'
                )}
              </AlertDialogAction>
            </div>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* ─────────────────────── Members Section ─────────────────────── */}
      <div className="mb-12">
        <div className="flex items-center justify-between mb-6">
          <SectionTitle title="Members" />
          <div className="flex items-center gap-3">
            <button
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-xl border-2 border-[#ff5635]/40 text-[#ff5635] text-sm hover:bg-[#ff5635]/5 transition-colors active:scale-[0.97]"
              style={{ fontWeight: 600, letterSpacing: '0.04em' }}
              onClick={handleLeaveTeam}
              disabled={isLeavingTeam || !currentTeamId || !currentUserId}
            >
              <LogOut className="w-3.5 h-3.5" />
              {isLeavingTeam ? 'LEAVING…' : 'LEAVE TEAM'}
            </button>
            <button
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-xl bg-[#2e024c] text-white text-sm hover:bg-[#3a0d5e] transition-colors active:scale-[0.97] disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ fontWeight: 600, letterSpacing: '0.04em' }}
              onClick={() => setIsAddMemberOpen(true)}
              disabled={!canInviteMembersEffective || isLeavingTeam || !currentTeamId}
            >
              <Plus className="w-3.5 h-3.5" />
              ADD MEMBER
            </button>
          </div>
        </div>

        {/* Add Member Dialog */}
        <Dialog
          open={isAddMemberOpen}
          onOpenChange={(open) => {
            setIsAddMemberOpen(open);
            if (!open && !isInvitingMember) {
              setInviteMemberName('');
              setInviteMemberEmail('');
              setInviteMemberRole('');
            }
          }}
        >
          <DialogContent className="max-w-[760px] rounded-2xl border border-gray-100 bg-white p-0 shadow-[0_16px_48px_rgba(16,24,40,0.16)]">
            <div className="px-10 py-8">
              <div className="space-y-6">
                <div>
                  <div className="text-[12px] text-[#667085] mb-2" style={{ fontWeight: 600 }}>
                    {t('teamPage.invite.fields.name')} <span className="text-[#ff5635]">*</span>
                  </div>
                  <Input
                    value={inviteMemberName}
                    onChange={(e) => setInviteMemberName(e.target.value)}
                    disabled={isInvitingMember}
                    aria-label={t('teamPage.invite.fields.name')}
                    className="h-12 rounded-xl text-base"
                  />
                </div>

                <div>
                  <div className="text-[12px] text-[#667085] mb-2" style={{ fontWeight: 600 }}>
                    {t('teamPage.invite.fields.email')} <span className="text-[#ff5635]">*</span>
                  </div>
                  <Input
                    value={inviteMemberEmail}
                    onChange={(e) => setInviteMemberEmail(e.target.value)}
                    disabled={isInvitingMember}
                    aria-label={t('teamPage.invite.fields.email')}
                    className="h-12 rounded-xl text-base"
                    inputMode="email"
                  />
                </div>

                <div>
                  <div className="text-[12px] text-[#667085] mb-2" style={{ fontWeight: 600 }}>
                    {t('teamPage.invite.role')} <span className="text-[#ff5635]">*</span>
                  </div>
                  <RoleDropdown
                    value={inviteMemberRole}
                    onChange={setInviteMemberRole}
                    disabled={isInvitingMember}
                    options={inviteTeamRoleOptions}
                    placeholder={t('teamPage.invite.selectRole')}
                    variant="field"
                    showDescriptions
                    ariaLabel={t('teamPage.invite.role')}
                  />
                </div>

                <Button
                  type="button"
                  onClick={() => void handleInviteMember()}
                  disabled={
                    isInvitingMember ||
                    inviteMemberName.trim().length === 0 ||
                    inviteMemberEmail.trim().length === 0 ||
                    !inviteMemberRole
                  }
                  className="h-12 w-full rounded-full bg-[#2e024c] hover:bg-[#3a0d5e] text-white font-semibold tracking-[0.12em] text-[12px] disabled:opacity-100 disabled:bg-[#d0d5dd] disabled:hover:bg-[#d0d5dd]"
                >
                  {isInvitingMember ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                      {t('teamPage.invite.actions.sendInvite')}
                    </>
                  ) : (
                    t('teamPage.invite.actions.sendInvite')
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Members Table */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-gray-100 bg-[#f8f9fa]/60">
            <div className="col-span-3">
              <span className="text-[10px] text-[#98a2b3] tracking-[0.06em]" style={{ fontWeight: 600, textTransform: 'uppercase' as const }}>
                {t('teamPage.members.columns.name')}
              </span>
            </div>
            <div className="col-span-3">
              <span className="text-[10px] text-[#98a2b3] tracking-[0.06em]" style={{ fontWeight: 600, textTransform: 'uppercase' as const }}>
                {t('teamPage.members.columns.email')}
              </span>
            </div>
            <div className="col-span-2 text-center">
              <span className="text-[10px] text-[#98a2b3] tracking-[0.06em]" style={{ fontWeight: 600, textTransform: 'uppercase' as const }}>
                {t('teamPage.members.columns.reviewsCuration')}
              </span>
            </div>
            <div className="col-span-2 text-center">
              <span className="text-[10px] text-[#98a2b3] tracking-[0.06em]" style={{ fontWeight: 600, textTransform: 'uppercase' as const }}>
                {t('teamPage.members.columns.reviewsDrafts')}
              </span>
            </div>
            <div className="col-span-1 text-right">
              <span className="text-[10px] text-[#98a2b3] tracking-[0.06em]" style={{ fontWeight: 600, textTransform: 'uppercase' as const }}>
                {t('teamPage.members.columns.role')}
              </span>
            </div>
            <div className="col-span-1" />
          </div>

          {/* Rows */}
          {members.map((member) => (
            <div
              key={member.id}
              className="grid grid-cols-12 gap-4 items-center px-5 py-4 border-b border-gray-50 last:border-b-0 hover:bg-[#f8f9fa]/40 transition-colors group"
            >
              {/* Name */}
              <div className="col-span-3 flex items-center gap-3 min-w-0">
                <MemberAvatar
                  member={member}
                />
                <span className="text-sm text-[#101828] truncate" style={{ fontWeight: 500 }}>
                  {member.name}
                  {member.isCurrentUser && (
                    <span className="text-[10px] text-[#98a2b3] ml-1.5">(you)</span>
                  )}
                </span>
              </div>

              {/* Email */}
              <div className="col-span-3 flex items-center justify-between gap-3 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  {(member.status ?? 'MEMBER') === 'MEMBER' ? (
                    <Mail className="w-3.5 h-3.5 text-[#98a2b3] shrink-0" />
                  ) : (
                    <Send className="w-3.5 h-3.5 text-[#98a2b3] shrink-0" />
                  )}
                  <span className="text-sm text-[#667085] truncate">{member.email}</span>
                </div>
                {(() => {
                  const status = member.status ?? 'MEMBER';
                  const invitedAt = member.dateInvited ? new Date(member.dateInvited).getTime() : NaN;
                  const isPending = status !== 'MEMBER';
                  const isOldInvite =
                    isPending &&
                    Number.isFinite(invitedAt) &&
                    (Date.now() - invitedAt) / 1000 > 86400;
                  const showRemind = isPending && isOldInvite && isAdmin && !member.isCurrentUser;
                  if (!isPending) return null;

                  if (showRemind) {
                    const isLoading = remindingMemberId === member.id;
                    return (
                      <button
                        type="button"
                        onClick={() => void handleRemindMember(member)}
                        disabled={Boolean(remindingMemberId) || Boolean(removingMemberId) || Boolean(updatingMemberId)}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-[10px] tracking-[0.10em] text-[#101828] hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
                        style={{ fontWeight: 700, textTransform: 'uppercase' as const }}
                      >
                        {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin text-[#be43ff]" aria-hidden /> : null}
                        Remind
                      </button>
                    );
                  }

                  return (
                    <span
                      className="text-[10px] text-[#98a2b3] tracking-[0.08em] shrink-0"
                      style={{ fontWeight: 600, textTransform: 'uppercase' as const }}
                    >
                      Pending
                    </span>
                  );
                })()}
              </div>

              {/* Reviews creators */}
              <div className="col-span-2 flex justify-center">
                <input
                  type="checkbox"
                  checked={member.isCurationReviewer}
                  disabled={
                    !canManageReviewers
                    || (member.status ?? 'MEMBER') !== 'MEMBER'
                    || updatingMemberId === member.id
                    || removingMemberId === member.id
                  }
                  onChange={(e) => void handleReviewerFlagChange(member.id, 'isCurationReviewer', e.target.checked)}
                  aria-label={t('teamPage.members.aria.reviewsCuration', { name: member.name })}
                  className="h-4 w-4 rounded border-gray-300 text-[#2e024c] focus:ring-[#2e024c]"
                />
              </div>

              {/* Reviews drafts */}
              <div className="col-span-2 flex justify-center">
                <input
                  type="checkbox"
                  checked={member.isDraftReviewer}
                  disabled={
                    !canManageReviewers
                    || (member.status ?? 'MEMBER') !== 'MEMBER'
                    || updatingMemberId === member.id
                    || removingMemberId === member.id
                  }
                  onChange={(e) => void handleReviewerFlagChange(member.id, 'isDraftReviewer', e.target.checked)}
                  aria-label={t('teamPage.members.aria.reviewsDrafts', { name: member.name })}
                  className="h-4 w-4 rounded border-gray-300 text-[#2e024c] focus:ring-[#2e024c]"
                />
              </div>

              {/* Role */}
              <div className="col-span-1 flex justify-end">
                <RoleDropdown
                  value={member.role}
                  onChange={(role) => handleRoleChange(member.id, role)}
                  disabled={!isAdmin || member.isCurrentUser || updatingMemberId === member.id || removingMemberId === member.id}
                  options={teamRoleOptions}
                  ariaLabel={t('teamPage.invite.role')}
                />
              </div>

              {/* Actions */}
              <div className="col-span-1 flex items-center justify-end gap-1">
                {!member.isCurrentUser && isAdmin && (
                  <button
                    onClick={() => handleRemoveMember(member.id)}
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-[#ff5635]/60 hover:text-[#ff5635] hover:bg-[#ff5635]/5 transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Remove member"
                    disabled={Boolean(removingMemberId) || Boolean(updatingMemberId)}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-[#98a2b3]"
                  title={!isAdmin ? 'Permissions locked' : member.isCurrentUser ? 'Owner — cannot change' : 'Permissions'}
                >
                  <Lock className="w-3.5 h-3.5" />
                </div>
              </div>
            </div>
          ))}

          {!isLoadingTeams && !isLoadingTeamData && members.length === 0 && (
            <div className="text-center py-12 text-[#98a2b3] text-sm">
              No members yet. Click "+ ADD MEMBER" to invite someone.
            </div>
          )}
        </div>
      </div>

      <TeamBrandSettingsSection
        teamId={currentTeamId}
        isAdmin={isAdmin}
        viewerUserId={currentUserId}
        members={members}
        setMembers={setMembers as TeamBrandSettingsSetMembers}
        brands={brands.map((b) => ({ id: b.id, name: b.name, logo: b.logo }))}
        logout={logout}
      />

      {/* Footer */}
      <footer className="mt-16 pt-8 border-t border-gray-100">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#98a2b3]">
          <span>&copy; 2026 Beatly. All rights reserved.</span>
          <div className="flex items-center gap-6">
            <a href="#" className="text-[#be43ff] hover:text-[#a830e0] transition-colors">Product updates</a>
            <a href={BEATLY_PRIVACY_POLICY_URL} target="_blank" rel="noreferrer" className="text-[#be43ff] hover:text-[#a830e0] transition-colors">Privacy Policy</a>
            <a href={BEATLY_TERMS_OF_SERVICE_URL} target="_blank" rel="noreferrer" className="text-[#be43ff] hover:text-[#a830e0] transition-colors">Terms of Service</a>
            <a href="#" className="text-[#be43ff] hover:text-[#a830e0] transition-colors">Cookie-settings</a>
          </div>
        </div>
      </footer>
    </div>
  );
};
